import React, { memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { RootState } from '../../../../store/store';
import { setSelectedModel, setSelectedTool } from '../../../../store/chatModeSlice';
import { getToolForModelSelection, getVisibleModelOptions, MODEL_OPTIONS } from '../../../../utils/config/modelConfig';
import { useTheme } from '../../../Theme/ThemeProvider';
import { ModelConfig } from '../../../../utils/config/modelConfig';

interface ModelSelectorProps {
visible: boolean;
  /** When provided, called instead of directly dispatching — allows the parent to run guards (e.g. file-upload warnings). */
  onModelSelect?: (modelId: string) => void;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ visible, onModelSelect }) => {
  const dispatch = useDispatch();
  const selectedModelId = useSelector((state: RootState) => state.chatMode.selectedModelId);
  const activeTool = useSelector((state: RootState) => state.chatMode.selectedTool);
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const visibleModels = getVisibleModelOptions(activeTool);
  const selectedModel =
    MODEL_OPTIONS.find(m => m.id === selectedModelId)
    ?? visibleModels.find(m => m.id === selectedModelId)
    ?? visibleModels[0];

  const handleModelSelection = React.useCallback((modelId: string) => {
    const toolForModel = getToolForModelSelection(modelId);
    if (onModelSelect) {
      onModelSelect(modelId);
    } else {
      if (toolForModel !== activeTool) {
        dispatch(setSelectedTool(toolForModel));
      }
      dispatch(setSelectedModel(modelId));
    }
    setIsOpen(false);
  }, [dispatch, onModelSelect, activeTool]);

  // Close on outside click
  React.useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Close dropdown when hiding
  React.useEffect(() => {
    if (!visible && isOpen) {
        setIsOpen(false)
    };
  }, [isOpen, visible]);

  return (
    <div ref={containerRef} className="relative inline-block" data-tutorial="model-selector" style={{ zIndex: 100, visibility: visible ? 'visible' : 'hidden', pointerEvents: visible ? 'auto' : 'none' }}>
      {/* Trigger button */}
      <button
        onClick={() => {
            setIsOpen(prev => !prev)
        }}
        className="flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
        style={{
          minWidth: '220px',
          maxWidth: '260px',
          color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#5E95ED',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #5E95ED',
          background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E8F3FF',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
          <span className="top-heavy" style={{ flexShrink: 0 }}>Model:&nbsp;&nbsp;</span>
          <span style={{ display: 'flex', alignItems: 'center', color: isDark ? '#82B6FA' : '#2563EB' }}>
            {selectedModel.icon && <span style={{ display: 'inline-flex', marginRight: '4px', color: isDark ? '#82B6FA' : '#2563EB' }}>{selectedModel.icon}</span>}
            <span className="top-heavy">{selectedModel.shortName ?? selectedModel.displayName}</span>
          </span>
        </span>
        <ChevronDownIcon
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.8, y: -4 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.8, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 mt-1 rounded-lg shadow-lg overflow-hidden origin-top py-1"
            style={{
              minWidth: '220px',
              width: containerRef.current?.offsetWidth ?? 'auto',
              background: isDark ? 'rgba(30, 30, 30, 0.95)' : '#FFFFFF',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #5E95ED',
              backdropFilter: 'blur(12px)',
            }}
            role="listbox"
            aria-activedescendant={selectedModelId}
          >
            {visibleModels.map((model: ModelConfig) => {
              const isSelected = model.id === selectedModelId;
              return (
                <button
                  key={model.id}
                  id={model.id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleModelSelection(model.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                  style={{
                    background: isSelected
                      ? 'var(--color-secondary)'
                      : 'transparent',
                    borderTop: isSelected ? (isDark ? '1px solid rgba(82, 160, 255, 0.3)' : '1px solid #93c5fd') : '1px solid transparent',
                    borderBottom: isSelected ? (isDark ? '1px solid rgba(82, 160, 255, 0.3)' : '1px solid #93c5fd') : '1px solid transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(82, 160, 255, 0.18)' : '#93c5fd'; }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected
                      ? ('var(--color-secondary)')
                      : 'transparent';
                  }}
                >
                  {model.icon && <span className="flex-shrink-0" style={{ color: isSelected ? (isDark ? '#82B6FA' : '#2563EB') : (isDark ? 'rgba(255,255,255,0.85)' : '#374151') }}>{model.icon}</span>}
                  <span className="flex flex-col">
                    <span className="top-heavy" style={{ display: 'inline-block', fontWeight: isSelected ? 600 : 400, color: isSelected ? (isDark ? '#82B6FA' : '#2563EB') : (isDark ? 'rgba(255,255,255,0.85)' : '#374151') }}>{model.shortName ?? model.displayName}</span>
                    {model.description && <span style={{fontWeight: isSelected ? 600 : 400, fontSize: '0.72rem', color: isDark ? 'rgba(255,255,255,0.45)' : '#9ca3af' }}>{model.description}</span>}
                  </span>
                </button>
              );
            })}


          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(ModelSelector);
